// frontend/src/components/CareJourneyTimeline.jsx
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/client';
import { Link2, ChevronDown, ChevronUp, ArrowUpRight } from 'lucide-react';

const EVENT_LABELS = {
  symptom_reported:      'Symptom reported',
  risk_assessment:       'Risk assessed',
  action_generated:      'Action generated',
  action_executed:       'Action completed',
  action_dismissed:      'Action dismissed',
  action_delegated:      'Action delegated',
  action_response:       'Action response',
  doctor_response:       'Doctor response',
  care_plan_updated:     'Care plan updated',
  appointment_scheduled: 'Appointment scheduled',
  appointment_completed: 'Appointment completed',
  appointment_missed:    'Appointment missed',
  caregiver_alert:       'Caregiver alert',
  inactivity_detected:   'Inactivity detected',
  medication_missed:     'Medication missed',
  medication_taken:      'Medication taken',
  medication_issue:      'Medication issue',
  medication_picked_up:  'Medication picked up',
  vital_threshold:       'Vital sign alert',
  mood_checkin:          'Mood check-in',
  mood_check_in:         'Mood check-in',
  data_consent_granted:  'Data consent granted',
  data_consent_revoked:  'Data consent revoked',
  left_care_team:        'Left care team',
  system_signal:         'System alert',
  emr_sync:              'Medical record sync',
  patient_distress:      'Patient distress',
  service_missed:        'Service missed',
  custom_report:         'Custom report',
};

function actorLabel(event) {
  if (event.source === 'patient') return 'Patient';
  // Doctor name embedded in data (from symptom.responses synthetic events)
  if (event.data?.doctorName) return `Dr. ${event.data.doctorName}`;
  const pb = event.processedBy || '';
  if (/^(risk[-_]engine|action[-_]engine|risk[-_]detection[-_]engine|escalation[-_]service|system)$/i.test(pb)) {
    return 'System (Automated)';
  }
  if (/^[a-f\d]{24}$/i.test(pb)) return 'Care team member';
  if (pb) return pb;
  return 'System';
}

function dotColor(event) {
  if (event.source === 'patient') return '#00478d';
  const pb = event.processedBy || '';
  if (/^(risk[-_]engine|action[-_]engine|risk[-_]detection[-_]engine|escalation[-_]service)$/i.test(pb)) {
    return '#f59e0b';
  }
  if (event.status === 'processed') return '#10b981';
  return '#94a3b8';
}

function fmt(date) {
  if (!date) return '—';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function TimelineItem({ label, actor, date, color, link, linkLabel, tag, isCurrent }) {
  return (
    <div className={`relative pl-5 ${isCurrent ? 'bg-blue-50 rounded-xl py-2 -mx-2 px-4 ml-3' : ''}`}>
      <div
        className="absolute left-0 top-1.5 w-2.5 h-2.5 rounded-full -translate-x-[5.5px] border-2 border-white"
        style={{ backgroundColor: color }}
      />
      <div className="flex items-center gap-1.5 flex-wrap">
        {link ? (
          <Link
            to={link}
            className={`text-sm font-bold hover:underline ${isCurrent ? 'text-primary' : 'text-slate-800'}`}
          >
            {label}
          </Link>
        ) : (
          <p className={`text-sm font-bold ${isCurrent ? 'text-primary' : 'text-slate-800'}`}>{label}</p>
        )}
        {tag === 'source' && (
          <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full">source</span>
        )}
        {isCurrent && (
          <span className="text-[10px] font-bold text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded-full">current</span>
        )}
      </div>
      <p className="text-xs text-slate-500 mt-0.5">{actor} · {fmt(date)}</p>
    </div>
  );
}

export default function CareJourneyTimeline({ symptomId, currentEventId }) {
  const [expanded, setExpanded] = useState(false);
  const [chain, setChain] = useState([]);
  const [symptom, setSymptom] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);

  useEffect(() => {
    if (!symptomId) return;
    let cancelled = false;
    setLoading(true);
    Promise.allSettled([
      api.get(`/care-events/chain/${symptomId}`),
      api.get(`/symptoms/${symptomId}`),
    ]).then(([chainRes, symptomRes]) => {
      if (cancelled) return;
      if (chainRes.status === 'fulfilled') {
        setChain(chainRes.value.data.data || []);
      }
      if (symptomRes.status === 'fulfilled') {
        const d = symptomRes.value.data;
        setSymptom(d.data || d.symptom || d);
      }
      setFetched(true);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [symptomId]);

  // Don't render if no symptomId, or if done loading and nothing to show
  if (!symptomId) return null;
  if (fetched && !loading && chain.length === 0 && !symptom) return null;

  const totalCount = chain.length + (symptom ? 1 : 0);
  const countLabel = `${totalCount} event${totalCount !== 1 ? 's' : ''}`;

  return (
    <div className="bg-white rounded-2xl shadow-premium border border-outline-variant/10 overflow-hidden">
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full px-7 py-5 flex items-center justify-between hover:bg-slate-50 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center flex-shrink-0">
            <Link2 className="w-5 h-5 text-violet-600" />
          </div>
          <div>
            <p className="text-xs font-extrabold text-slate-500 uppercase tracking-[0.2em]">Care Journey</p>
            <p className="text-sm font-bold text-slate-900">
              {loading ? 'Loading…' : countLabel}
            </p>
          </div>
        </div>
        {expanded
          ? <ChevronUp className="w-4 h-4 text-slate-400 flex-shrink-0" />
          : <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
        }
      </button>

      {expanded && (
        <div className="border-t border-slate-100 px-7 py-5">
          {loading ? (
            <div className="h-4 bg-slate-100 rounded animate-pulse w-3/4" />
          ) : (
            <div className="relative border-l border-slate-200 space-y-4 ml-1.5">
              {symptom && (
                <TimelineItem
                  label="Symptom reported"
                  actor="Patient"
                  date={symptom.createdAt}
                  color="#00478d"
                  link={`/symptom/${symptomId}`}
                  linkLabel="Open symptom"
                  tag="source"
                />
              )}
              {chain.map(event => {
                const id = event._id?.toString();
                const isCurrent = id === currentEventId;
                // For doctor suggestions stored as action_generated, use a friendlier label
                const label = event.eventType === 'action_generated' && event.data?.responseType === 'suggestion'
                  ? 'Doctor suggestion'
                  : (EVENT_LABELS[event.eventType] || event.eventType);
                return (
                  <TimelineItem
                    key={id}
                    label={label}
                    actor={actorLabel(event)}
                    date={event.createdAt}
                    color={dotColor(event)}
                    link={isCurrent ? null : `/care-events/${id}`}
                    linkLabel="Open event"
                    isCurrent={isCurrent}
                  />
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
